import uniqIDGen from '@skyhookadventure/unique-id';
import Uppy from '@uppy/core';
import Dropbox from '@uppy/dropbox';
import Facebook from '@uppy/facebook';
import GoogleDrive from '@uppy/google-drive';
import Instagram from '@uppy/instagram';
import OneDrive from '@uppy/onedrive';
import Transloadit, {
  COMPANION_ALLOWED_HOSTS,
  COMPANION_URL,
} from '@uppy/transloadit';
import Unsplash from '@uppy/unsplash';
import mime from 'mime';
import { useEffect, useState } from 'react';
import { RequireAtLeastOne } from 'type-fest';

export type RequireUserIdOrHostId = RequireAtLeastOne<{
  userID: string;
  hostID: string;
}>;

type CreateFilenameProps = {
  /** Note the fileName default is a Transloadit template, not a template literal string */
  fileName?: string;
} & RequireUserIdOrHostId;

/**
 * Create the filename for a transloadit file
 */
export function createFilename({
  hostID,
  userID,
  fileName = `\${file.url_name}`,
}: CreateFilenameProps) {
  // Path determines how the file is saved on aws.
  //
  // https://transloadit.com/docs/transcoding/file-exporting/s3-store/
  // https://transloadit.com/docs/topics/assembly-instructions/#form-fields-in-instructions
  // Currently files are stored at the root of the bucket.
  const prefix = hostID ?? userID;
  return `${prefix}/${fileName}`;
}

type UseUppyProps = RequireUserIdOrHostId & {
  allowMultipleSelect: boolean;
  hostID?: string;
  userID?: string;
};

/**
 * Create an Uppy Instance
 */
export function useUppy({
  allowMultipleSelect = false,
  hostID,
  userID,
}: UseUppyProps): [Uppy, boolean] {
  const [uppy, setUppy] = useState<Uppy>();

  useEffect(() => {
    const uppyInstance = new Uppy({
      autoProceed: true,
      restrictions: {
        allowedFileTypes: ['image/*'],
        // Set max files to at most 30, to avoid overloading the backend
        maxNumberOfFiles: allowMultipleSelect ? 30 : 1,
      },
      onBeforeUpload: (files) => {
        // We’ll be careful to return a new object, not mutating the original `files`
        const updatedFiles = {};
        Object.keys(files).forEach((fileID) => {
          // determine the extension from file type and can't fallback to extension from file name
          const extBefore =
            mime.getExtension(files[fileID].type) || files[fileID].extension;
          // if the extension is jpeg, we want to use jpg instead for the
          // filename, this is because jpeg will be converted to jpg by
          // transloadit
          const ext = extBefore === 'jpeg' ? 'jpg' : extBefore;
          // generate a new filename for the media item and use that as the
          // filename for storing on AWS S3
          const fileName = `${uniqIDGen('media')}${ext ? `.${ext}` : ''}`;

          updatedFiles[fileID] = {
            ...files[fileID],
            name: fileName,
            ext,
          };
        });
        return updatedFiles;
      },
    })
      .use(Transloadit, {
        waitForEncoding: true,
        waitForMetadata: true,
        params: {
          auth: {
            key: process.env.NEXT_PUBLIC_TRANSLOADIT,
          },
          steps: {
            ':original': {
              robot: '/upload/handle',
            },
            exported: {
              use: ':original',
              robot: '/s3/store',
              credentials: 'Media Bucket',
              url_prefix: 'https://demos.transloadit.com/',
              path: createFilename({ hostID, userID }),
            },
          },
        },
      })
      .use(Facebook, {
        id: 'facebook',
        companionUrl: COMPANION_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
      })
      .use(GoogleDrive, {
        id: 'google-drive',
        companionUrl: COMPANION_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
      })
      .use(Dropbox, {
        id: 'dropbox',
        companionUrl: COMPANION_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
      })
      .use(OneDrive, {
        id: 'onedrive',
        companionUrl: COMPANION_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
      })
      .use(Instagram, {
        id: 'instagram',
        companionUrl: COMPANION_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
      })
      .use(Unsplash, {
        id: 'unsplash',
        companionUrl: COMPANION_URL,
      });

    setUppy(uppyInstance);

    // Clean up on unmount
    return () => {
      uppy?.close({ reason: 'unmount' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowMultipleSelect, hostID, userID]);

  const isLoading = !uppy;

  return [uppy, isLoading];
}
